import { useEffect, useRef, useState } from 'react';

/**
 * How long a gate will hold itself shut against a send it believes is running.
 *
 * Long enough that a normal send always finishes inside it, short enough that a
 * reader on a dead connection is not kept waiting on one.
 */
const SEND_BLOCK_TIMEOUT_MS = 15_000;

/**
 * Whether a send in flight should still block dismissal.
 *
 * A send must not be dismissed by a stray tap or swipe — the enquiry is already
 * on its way and closing over it loses the confirmation. But a fetcher has no
 * timeout of its own, so holding the modal shut for as long as one claims to be
 * running made a stalled connection permanent: close, escape, outside tap and
 * swipe all dead, with a page reload the only way out.
 *
 * Returns true only while the send could plausibly still be running.
 */
export const useSendBlock = (sendInFlight: boolean): boolean => {
    const [expired, setExpired] = useState(false);
    /** When the current run of in-flight frames began. */
    const startedAtRef = useRef<number | null>(null);

    useEffect(() => {
        if (!sendInFlight) {
            startedAtRef.current = null;
            setExpired(false);
            return;
        }
        // Measured from the FIRST in-flight frame, not from each one. A retry
        // cycle that dips back to idle between attempts would otherwise restart
        // the clock every time, so on a flapping connection the hold never
        // expired — the exact trap this exists to open.
        const startedAt = startedAtRef.current ?? Date.now();
        startedAtRef.current = startedAt;
        const remaining = SEND_BLOCK_TIMEOUT_MS - (Date.now() - startedAt);
        if (remaining <= 0) {
            setExpired(true);
            return;
        }
        const timer = setTimeout(() => setExpired(true), remaining);

        return () => clearTimeout(timer);
    }, [sendInFlight]);

    return sendInFlight && !expired;
};
